/**
 * The garden as a syncable envelope (SPEC-022 D-1).
 *
 * The mechanism is habb.me's: a revision-versioned document whose `syncMeta`
 * block is byte-compatible with `~/projects/pwa/src/domain/sync/syncEnvelope.js`,
 * because the Firestore ruleset — owned by that repo (CON-003) — validates the
 * two products' envelopes with the same helpers. A field added or renamed here
 * is a rules change there first.
 *
 * The payload is not habb.me's: one slot, a list of plant ids. That is also why
 * conflicts need no dialog (SPEC-022 D-2) — two gardens merge by set union,
 * remote order first, and the only accepted loss is that a removal raced by
 * another device's edit can come back.
 *
 * Pure functions only. Time arrives as an argument; storage and Firestore live
 * in `application/` and `infrastructure/`.
 */

export const GARDEN_SCHEMA_VERSION = 1;

/**
 * The rules' `hasSyncMetaShape` admits exactly these keys. The emulator suite
 * in `~/projects/pwa/firestore.plant-garden.rules.emulator.test.js` builds its
 * fixtures from a MIRROR of `createCloudGardenEnvelope` below; this list is the
 * contract both are written against, and `gardenEnvelope.test.js` pins it.
 */
export const SYNC_META_FIELDS = [
    'deviceId',
    'revision',
    'updatedAt',
    'updatedByDeviceId',
    'pendingSync',
    'baseRevision',
    'lastSyncedRevision',
    'lastSyncedAt',
];

/**
 * Matches the rules' `isDeviceId` pattern: `^device-[a-z0-9-]+$`.
 */
export function createGardenDeviceId(randomValue = Math.random()) {
    const suffix = randomValue.toString(36).slice(2, 10) || '0';

    return `device-plant-${suffix}`;
}

function sanitizePlantIds(value) {
    return Array.isArray(value) ? value.filter((id) => typeof id === 'string') : [];
}

function asNonNegativeInt(value) {
    return Number.isInteger(value) && value >= 0 ? value : 0;
}

function asIsoTimestamp(value, fallback) {
    return typeof value === 'string' && value ? value : fallback;
}

export function createEmptyGardenEnvelope({ deviceId, now }) {
    return {
        schemaVersion: GARDEN_SCHEMA_VERSION,
        garden: { plantIds: [] },
        syncMeta: {
            deviceId,
            revision: 0,
            updatedAt: now,
            updatedByDeviceId: deviceId,
            pendingSync: false,
            baseRevision: 0,
            lastSyncedRevision: 0,
            lastSyncedAt: now,
        },
    };
}

/**
 * Anything can be in storage — a half-written value, or a key another version
 * of the app wrote — so a bad envelope reads as an empty garden rather than
 * taking the screen down (the same posture `parseGarden` has held since
 * SPEC-014).
 */
export function normalizeGardenEnvelope(raw, { deviceId, now }) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return createEmptyGardenEnvelope({ deviceId, now });
    }

    const garden = raw.garden && typeof raw.garden === 'object' ? raw.garden : {};
    const syncMeta = raw.syncMeta && typeof raw.syncMeta === 'object' ? raw.syncMeta : {};
    const revision = asNonNegativeInt(syncMeta.revision);
    const lastSyncedRevision = Math.min(asNonNegativeInt(syncMeta.lastSyncedRevision), revision);

    return {
        schemaVersion: GARDEN_SCHEMA_VERSION,
        garden: { plantIds: sanitizePlantIds(garden.plantIds) },
        syncMeta: {
            deviceId: typeof syncMeta.deviceId === 'string' && syncMeta.deviceId ? syncMeta.deviceId : deviceId,
            revision,
            updatedAt: asIsoTimestamp(syncMeta.updatedAt, now),
            updatedByDeviceId: typeof syncMeta.updatedByDeviceId === 'string' && syncMeta.updatedByDeviceId
                ? syncMeta.updatedByDeviceId
                : deviceId,
            pendingSync: syncMeta.pendingSync === true,
            baseRevision: Math.min(asNonNegativeInt(syncMeta.baseRevision), revision),
            lastSyncedRevision,
            lastSyncedAt: asIsoTimestamp(syncMeta.lastSyncedAt, now),
        },
    };
}

/**
 * A local change: the new list, a bumped revision, and the dirty flag that IS
 * the sync queue — there is no outbox, `pendingSync: true` is what a retry or
 * a reconnect looks for.
 */
export function createNextGardenEnvelope(envelope, { plantIds, deviceId, now }) {
    return {
        ...envelope,
        garden: { plantIds: sanitizePlantIds(plantIds) },
        syncMeta: {
            ...envelope.syncMeta,
            deviceId,
            revision: envelope.syncMeta.revision + 1,
            updatedAt: now,
            updatedByDeviceId: deviceId,
            pendingSync: true,
        },
    };
}

/**
 * The document the cloud write sends, and nothing else: `pendingSync` already
 * false and the revisions aligned, because the rules refuse a document that
 * describes itself as unsynced (`hasSyncMetaShape`). `baseRevision` carries
 * what this device last saw synced — the compare half of the rules'
 * compare-and-set.
 *
 * MIRRORED by `createSyncedGardenDocument` in
 * `~/projects/pwa/firestore.plant-garden.rules.emulator.test.js`. A shape
 * change here must land there in the same change, or the rules will deny in
 * production what that suite approved.
 */
export function createCloudGardenEnvelope(envelope, { now }) {
    return {
        schemaVersion: envelope.schemaVersion,
        garden: { plantIds: [...envelope.garden.plantIds] },
        syncMeta: {
            deviceId: envelope.syncMeta.deviceId,
            revision: envelope.syncMeta.revision,
            updatedAt: envelope.syncMeta.updatedAt,
            updatedByDeviceId: envelope.syncMeta.updatedByDeviceId,
            pendingSync: false,
            baseRevision: envelope.syncMeta.lastSyncedRevision,
            lastSyncedRevision: envelope.syncMeta.revision,
            lastSyncedAt: now,
        },
    };
}

export function markGardenEnvelopeSynced(envelope, { now }) {
    return {
        ...envelope,
        syncMeta: {
            ...envelope.syncMeta,
            pendingSync: false,
            baseRevision: envelope.syncMeta.revision,
            lastSyncedRevision: envelope.syncMeta.revision,
            lastSyncedAt: now,
        },
    };
}

/**
 * Adopt a remote envelope wholesale, as this device's local copy.
 */
export function createAppliedRemoteGardenEnvelope(remoteEnvelope, { deviceId, now }) {
    const normalized = normalizeGardenEnvelope(remoteEnvelope, { deviceId, now });

    return {
        ...normalized,
        syncMeta: {
            ...normalized.syncMeta,
            deviceId,
            pendingSync: false,
            baseRevision: normalized.syncMeta.revision,
            lastSyncedRevision: normalized.syncMeta.revision,
            lastSyncedAt: now,
        },
    };
}

/**
 * The union of two gardens: remote order first, then local-only ids in their
 * local order (SPEC-022 D-2). Remote order wins the prefix because on the My
 * Garden screen — newest last in storage, reversed for display — the remote
 * list is the one another device deliberately arranged.
 */
export function mergeGardenPlantIds(remotePlantIds, localPlantIds) {
    const seen = new Set();
    const takeUnseen = (id) => {
        if (seen.has(id)) {
            return false;
        }

        seen.add(id);
        return true;
    };

    return [
        ...sanitizePlantIds(remotePlantIds).filter(takeUnseen),
        ...sanitizePlantIds(localPlantIds).filter(takeUnseen),
    ];
}

/**
 * What to do with a freshly read remote envelope. The same four-way answer as
 * habb.me's `getRemoteSyncDecision`, except that its `'conflict'` — local
 * pending AND remote moved on — is `'merge'` here, because a garden resolves
 * by union with nobody asked (D-2).
 *
 *   - `'same'`        — remote is what this device last synced and nothing is
 *                       pending; do nothing.
 *   - `'keep-local'`  — nothing new remotely but local changes are pending;
 *                       push.
 *   - `'take-remote'` — remote moved on and nothing is pending locally; adopt.
 *   - `'merge'`       — both sides moved; union and push.
 */
export function getRemoteGardenDecision(localEnvelope, remoteEnvelope) {
    const local = localEnvelope.syncMeta;

    if (!remoteEnvelope) {
        return local.pendingSync || local.revision > 0 ? 'keep-local' : 'same';
    }

    const remoteRevision = asNonNegativeInt(remoteEnvelope.syncMeta && remoteEnvelope.syncMeta.revision);

    if (remoteRevision > local.lastSyncedRevision) {
        return local.pendingSync ? 'merge' : 'take-remote';
    }

    return local.pendingSync ? 'keep-local' : 'same';
}

/**
 * Both sides moved: the union becomes the new local truth, based on the
 * remote revision so the next save's compare-and-set matches the stored
 * document, and strictly above it so the rules accept the advance.
 */
export function resolveGardenMerge(localEnvelope, remoteEnvelope, { deviceId, now }) {
    const remote = normalizeGardenEnvelope(remoteEnvelope, { deviceId, now });
    const mergedPlantIds = mergeGardenPlantIds(remote.garden.plantIds, localEnvelope.garden.plantIds);

    return {
        ...localEnvelope,
        garden: { plantIds: mergedPlantIds },
        syncMeta: {
            ...localEnvelope.syncMeta,
            deviceId,
            revision: Math.max(localEnvelope.syncMeta.revision, remote.syncMeta.revision) + 1,
            updatedAt: now,
            updatedByDeviceId: deviceId,
            pendingSync: true,
            baseRevision: remote.syncMeta.revision,
            lastSyncedRevision: remote.syncMeta.revision,
            lastSyncedAt: localEnvelope.syncMeta.lastSyncedAt,
        },
    };
}
